<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

interface SupplementRow { label: string; value: string }
interface SupplementData { engineer: string; updatedAt: string; note: string; rows: SupplementRow[] }

const props = defineProps<{ productKey: string }>()
const editing = ref(false)
const saved = ref<SupplementData | null>(null)
const engineer = ref('')
const note = ref('')
const rows = ref<SupplementRow[]>([])

function storageKey() { return `bf-engineer-supplement:${props.productKey}` }

function load() {
  const raw = window.localStorage.getItem(storageKey())
  saved.value = raw ? JSON.parse(raw) as SupplementData : null
  engineer.value = saved.value?.engineer ?? ''
  note.value = saved.value?.note ?? ''
  rows.value = saved.value?.rows.map((row) => ({ ...row })) ?? []
}

function addRow() { rows.value.push({ label: '', value: '' }) }
function removeRow(index: number) { rows.value.splice(index, 1) }

function save() {
  const data: SupplementData = {
    engineer: engineer.value.trim() || '未署名工程师',
    updatedAt: new Date().toLocaleString('zh-CN', { hour12: false }),
    note: note.value.trim(),
    rows: rows.value.filter((row) => row.label.trim() && row.value.trim()).map((row) => ({ label: row.label.trim(), value: row.value.trim() }))
  }
  window.localStorage.setItem(storageKey(), JSON.stringify(data))
  saved.value = data
  editing.value = false
}

function cancel() { load(); editing.value = false }

watch(() => props.productKey, () => { editing.value = false; load() })
onMounted(load)
</script>

<template>
  <section class="engineer-supplement">
    <header>
      <div><span>ENGINEER INPUT</span><h4>工程师补充信息</h4></div>
      <button v-if="!editing" type="button" @click="editing = true">{{ saved ? '继续完善' : '补充信息' }}</button>
    </header>

    <template v-if="!editing">
      <dl v-if="saved?.rows.length" class="engineer-supplement-values">
        <div v-for="row in saved.rows" :key="row.label"><dt>{{ row.label }}</dt><dd>{{ row.value }}</dd></div>
      </dl>
      <p v-if="saved?.note" class="engineer-note">{{ saved.note }}</p>
      <small v-if="saved">由 {{ saved.engineer }} 补充 · {{ saved.updatedAt }} · 本机离线保存</small>
      <p v-else class="engineer-empty">可逐步补充点位、通讯、供电、尺寸、选型注意事项等未完整信息。</p>
    </template>

    <form v-else @submit.prevent="save">
      <label>工程师姓名<input v-model="engineer" type="text" placeholder="例如：杨宇" /></label>
      <div v-for="(row, index) in rows" :key="index" class="engineer-row">
        <input v-model="row.label" type="text" placeholder="参数名称" />
        <input v-model="row.value" type="text" placeholder="参数内容" />
        <button type="button" aria-label="删除补充参数" @click="removeRow(index)">×</button>
      </div>
      <button class="engineer-add" type="button" @click="addRow">＋ 添加参数</button>
      <label>工程备注<textarea v-model="note" rows="3" placeholder="填写应用限制、选型说明或待核实内容"></textarea></label>
      <div class="engineer-actions"><button type="submit">保存到本机</button><button type="button" @click="cancel">取消</button></div>
      <small>补充内容与网站原始资料分开保存，不会覆盖原始数据。</small>
    </form>
  </section>
</template>
